import axios from 'axios';
import router from '@/router';

// Utils
import { getCookie, deleteCookie } from '@utils/cookies';

const instance = axios.create({
  baseURL: `${process.env.VUE_APP_BASE_DOMAIN}/v1`
});

// Logout handler function
const handleLogout = async (redirectToLogin = true) => {
  // Clear JWT cookie
  deleteCookie('jwt');

  // Reset user state in store if available
  if (window.$store) {
    await window.$store.dispatch('user/resetState');
  }

  // Redirect to login page if needed
  if (redirectToLogin) {
    window.location.replace(`${process.env.VUE_APP_FE_DOMAIN}/auth/login`);
  }
};

// Setting bearer from cookie before every api call
instance.interceptors.request.use(config => {
  const jwt = getCookie('jwt');
  config.headers.Authorization = `Bearer ${jwt}`;

  return config;
});

// Define critical endpoints that should trigger the error page when they fail
const CRITICAL_ENDPOINTS = [
  // Core data endpoints - app can't function without these
  '/user/', // User data is essential
  '/settings', // App settings
  '/services' // Core services

  // Do NOT include authentication endpoints
  // Do NOT include component-specific data endpoints
];

// Intercepting every response and formatting it for FE
instance.interceptors.response.use(
  response => {
    const formattedResponse = {
      ...response.data
    };

    return formattedResponse;
  },
  error => {
    // Check for 401 Unauthorized errors on any endpoint except auth endpoints
    // if (error.response && error.response.status === 401 && !error.config.url.includes('/auth/')) {
    //   console.error('Unauthorized access detected. Logging out.');
    //   handleLogout(true);
    //   return Promise.reject(error);
    // }

    // 1. Is this an auth-related endpoint? Skip error page redirect
    const isAuthEndpoint = error.config.url.includes('/auth/');

    // 2. Is this a critical endpoint?
    const isCriticalEndpoint = CRITICAL_ENDPOINTS.some(endpoint => error.config.url.includes(endpoint));

    // 3. Are we on an auth page? Skip error page redirect
    const currentRouteName = router?.currentRoute?.name;
    const isAuthRoute = ['authLogin', 'authRegister', 'auth0Callback'].includes(currentRouteName);

    // 4. Check for specific critical errors
    const isServerError = error.response && error.response.status >= 500;
    const isNotFoundError = error.response && error.response.status === 404;

    // 5. Only redirect for critical endpoints with server errors (and not on auth pages)
    if (isCriticalEndpoint && isServerError && !isAuthRoute && !isAuthEndpoint) {
      console.error(`Critical endpoint ${error.config.url} failed with status ${error.response.status}`);
      router.push({ name: 'serverError' });
    }
    // 6. For 404 errors on critical endpoints
    else if (isCriticalEndpoint && isNotFoundError && !isAuthRoute && !isAuthEndpoint) {
      console.error(`Critical resource not found at ${error.config.url}`);
      router.push({ name: 'notFound' });
    }

    // Always reject the promise so components can handle errors at their level
    return Promise.reject(error);
  }
);

export const getUri = ({ params = '', url }) => {
  return `${process.env.VUE_APP_BASE_DOMAIN}/v1/${url}?__bearer_access_token=${getCookie('jwt')}&${params}`;
};

export default instance;

export { MediaApi } from './modules/media';
export { OrderApi } from './modules/order';
export { PackagesApi } from './modules/package';
export { ReportApi } from './modules/report';
export { ServiceApi } from './modules/services';
export { SettingsApi } from './modules/settings';
export { ShootsApi } from './modules/shoots';
export { UserApi } from './modules/user';
export { QuotesApi } from './modules/quotes';
export { ConversationsApi } from './modules/conversations';
export { IntegrationsApi } from './modules/integrations';
export { ContentApi } from './modules/content.js';
export { MetaApi } from './modules/meta';
export { ReplicateApi } from './modules/replicate';
export { AuthApi } from './modules/auth';
export { PaymentMethodApi } from './modules/paymentmethod';
export { SubscriptionApi } from './modules/subscription';
export { BillingsApi } from './modules/billings';
export { ShareApi } from './modules/share';
